import React, { useContext, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl-next';
import type { LoomVideo } from '@loomhq/record-sdk';
import { useCrossFlow, Targets, Journeys } from '@atlassiansox/cross-flow-support';

import VideoIcon from '@atlaskit/icon/core/video';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import { Box, Inline, Text, Flex, Pressable, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Lozenge from '@atlaskit/lozenge';

import { SPAViewContext } from '@confluence/spa-view-context';
import { Attribution } from '@confluence/error-boundary';
import { getMonitoringClient } from '@confluence/monitoring';
import {
	LoomAdControl,
	useLoomRecorderEntryPoint,
	useLoomAdControlState,
	useLoomEntryPointVariant,
	useLoomEntryPointErrorCallback,
	useLoomRecorderTriggerListener,
	useLoomRecorderTrigger,
	LoomRecorderTriggerEvent,
	type LoomRecorderTrigger,
	LoomRecorderLoadingBlanket,
	useTriggerCrossFlowSuccessCheck,
	useLoomRecorderLoadingBlanketActions,
} from '@confluence/loom-utils';
import { useLoomInsertUrlActions } from '@confluence/loom-insert-url-store';
import { type FlagsStateContainer } from '@confluence/flags';
import { getUrlForContentType } from '@confluence/content-types-utils';
import { useSpaceKey } from '@confluence/space-utils';
import { useEditPageLoadingActions } from '@confluence/editor-loading-context';

export const CONTENT_TYPE = 'page';
export const ENTRY_POINT_ID = 'loom-confluence-read-time-dropdown-loom-script-extension';
export const SOURCE_COMPONENT = 'readTimeDropdownLoomRecording';

import { messages } from '../messages';

import { ViewScriptButton } from './ViewScriptButton';

const marginBottom = xcss({
	marginBottom: 'space.100',
});
const pressableContainer = xcss({
	paddingTop: 'space.100',
	paddingBottom: 'space.100',
	background: token('color.background.neutral.subtle'),
	':hover': {
		background: token('color.background.neutral.subtle.hovered'),
	},
	':active': {
		background: token('color.background.neutral.subtle.pressed'),
	},
	width: '440px',
});

const pressableButton = xcss({
	background: token('color.background.neutral.subtle'),
	':hover': {
		background: token('color.background.neutral.subtle'),
	},
	':active': {
		background: token('color.background.neutral.subtle'),
	},
	display: 'flex',
	alignItems: 'center',
	padding: 'space.0',
});

const HorizontalDivider = xcss({
	borderBottom: `1px solid ${token('color.border')}`,
	height: '1px',
	width: '135px',
});

export type LoadEditor = ({ args }: { [key: string]: string }) => void;

export type UseLoomScriptRecordingProps = {
	contentId: string;
	flags: FlagsStateContainer;
};

export const ReadTimeDropdownLoomScriptExtension = ({
	contentId,
	flags,
}: UseLoomScriptRecordingProps) => {
	const { loadEditor } = useEditPageLoadingActions();
	const { error, loading, entryPointVariant } = useLoomEntryPointVariant();
	const loomInsertUrlActions = useLoomInsertUrlActions();
	const spaceKey = useSpaceKey() || '';
	const crossFlow = useCrossFlow();
	const loomEntryPointErrorCallback = useLoomEntryPointErrorCallback({ flags });
	const { showBlanket, hideBlanket } = useLoomRecorderLoadingBlanketActions();
	const { isSiteAdmin } = useContext(SPAViewContext);
	const { triggerCrossFlowSuccessCheck } = useTriggerCrossFlowSuccessCheck({
		sourceComponent: SOURCE_COMPONENT,
	});

	const { triggerLoomRecorder } = useLoomRecorderTrigger();

	const redirectUrl =
		getUrlForContentType({
			contentType: CONTENT_TYPE,
			spaceKey,
			contentId,
			redirectToEditor: true,
		}) || '';

	const saveLoomUrlAndLoadEditor = useCallback(
		(video: LoomVideo) => {
			loomInsertUrlActions.updateFullPageEditorLoomUrl(video);

			loadEditor &&
				loadEditor({
					contentId,
					spaceKey,
					redirectUrl,
					contentType: CONTENT_TYPE,
				});
		},
		[loomInsertUrlActions, contentId, spaceKey, loadEditor, redirectUrl],
	);

	const { isInterestedInLoom, notInterestedInLoomCallback } = useLoomAdControlState({
		touchpoint: ENTRY_POINT_ID,
	});

	const handleNotInterested = useCallback(async () => {
		await notInterestedInLoomCallback();
	}, [notInterestedInLoomCallback]);

	const openLoomRecorderParams = useMemo(
		() => ({
			onInsert: (_: unknown, video: LoomVideo) => {
				saveLoomUrlAndLoadEditor(video);
			},
			// intentionally not localized because Loom RecordSDK does not support i18n
			insertButtonText: 'Insert Loom video',
		}),
		[saveLoomUrlAndLoadEditor],
	);

	const { openLoomRecorder } = useLoomRecorderEntryPoint({
		entryPointLocation: SOURCE_COMPONENT,
		flags,
		resetInsertFunctionOnUnmount: false,
	});

	// Listener: auto-trigger SDK if redirected back from Cross-flow
	useLoomRecorderTriggerListener(
		{
			event: LoomRecorderTriggerEvent.CROSS_FLOW_SUCCESS,
			entryPointId: SOURCE_COMPONENT,
		},
		openLoomRecorderParams,
		{
			entryPointLocation: SOURCE_COMPONENT,
			flags,
			resetInsertFunctionOnUnmount: false,
		},
	);

	const onRecordVideoClick = useCallback(() => {
		void openLoomRecorder(openLoomRecorderParams);
	}, [openLoomRecorder, openLoomRecorderParams]);

	const openFn = crossFlow.isEnabled ? crossFlow.apiV1.open : null;

	const openLoomCrossFlow = useCallback(async () => {
		if (!openFn) return;

		try {
			showBlanket(SOURCE_COMPONENT);

			const { success } = await openFn({
				journey: Journeys.GET_STARTED,
				sourceComponent: SOURCE_COMPONENT,
				sourceContext: 'recordAVideo',
				targetProduct: Targets.LOOM,
				v1: true,
			});

			if (isSiteAdmin && success) {
				const trigger: LoomRecorderTrigger = {
					event: LoomRecorderTriggerEvent.CROSS_FLOW_SUCCESS,
					entryPointId: SOURCE_COMPONENT,
				};

				triggerLoomRecorder(trigger);

				triggerCrossFlowSuccessCheck({
					onCrossflowFailure: () => {
						hideBlanket();
					},
				});
			} else {
				hideBlanket();
			}
		} catch (error: any) {
			hideBlanket();
			getMonitoringClient().submitError(error, {
				attribution: Attribution.CONTENT_TYPES,
			});
		}
	}, [
		openFn,
		showBlanket,
		hideBlanket,
		isSiteAdmin,
		triggerLoomRecorder,
		triggerCrossFlowSuccessCheck,
	]);

	const handleLoomItemClick = useCallback(() => {
		if (error) {
			loomEntryPointErrorCallback();
			return;
		}
		if (entryPointVariant === 'CROSS_FLOW') {
			void openLoomCrossFlow();
		}
		if (entryPointVariant === 'CO_USE') {
			onRecordVideoClick();
		}
	}, [
		entryPointVariant,
		openLoomCrossFlow,
		onRecordVideoClick,
		loomEntryPointErrorCallback,
		error,
	]);

	if (!isInterestedInLoom) {
		return null;
	}

	return (
		<>
			<Box key="loomAIScriptSectionLabel" xcss={marginBottom}>
				<Inline alignBlock="center" spread="space-between">
					<Text color="color.text.subtlest" size="small">
						<FormattedMessage {...messages.readTimeImprovementLabel} />
					</Text>
					<Box xcss={HorizontalDivider} />
				</Inline>
			</Box>

			<Box key="createSummaryLoomVideo">
				<Pressable onClick={handleLoomItemClick} isDisabled={loading} xcss={pressableContainer}>
					<Flex justifyContent="space-between" alignItems="center">
						<Flex alignItems="center" gap="space.100">
							<VideoIcon size="small" label="Loom" />
							<FormattedMessage {...messages.readTimeLoomAIScriptTitle} />
							{entryPointVariant === 'CROSS_FLOW' && (
								<Lozenge appearance="new">
									<FormattedMessage {...messages.tryLozenge} />
								</Lozenge>
							)}
						</Flex>
						<Pressable xcss={pressableButton} onClick={(e) => e.stopPropagation()}>
							<ViewScriptButton />
							{entryPointVariant === 'CROSS_FLOW' && (
								<LoomAdControl
									entryPointId={ENTRY_POINT_ID}
									onDismiss={handleNotInterested}
									spacing="default"
								/>
							)}
						</Pressable>
					</Flex>
				</Pressable>
			</Box>

			<LoomRecorderLoadingBlanket entryPointId={SOURCE_COMPONENT} />
		</>
	);
};
